import {
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { Dispatch } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { errorActions } from "../../../store/errorSlice";
import { useAppDispatch } from "../../../store/hooks";
import {
  BusinessEntry,
  BusinessEntryFormError,
  BUSINESS_MAX_UPLOADS_SIZE,
} from "./CnamRequest";

interface BusinessEntriesInterface {
  businessEntries: BusinessEntry[];
  setBusinessEntries: Dispatch<React.SetStateAction<BusinessEntry[]>>;
  businessEntriesErrors: BusinessEntryFormError[];
  setBusinessEntriesErrors: Dispatch<
    React.SetStateAction<BusinessEntryFormError[]>
  >;
}

export const BusinessEntryForm = ({
  businessEntries,
  setBusinessEntries,
  businessEntriesErrors,
  setBusinessEntriesErrors,
}: BusinessEntriesInterface) => {
  const dispatch = useAppDispatch();

  const addBusinessEntry = () => {
    if (businessEntries.length >= 20) {
      dispatch(
        errorActions.setError({
          errorMessage: "Maximum 20 business entries allowed",
        })
      );
      return;
    }
    setBusinessEntries([
      ...businessEntries,
      {
        id: Date.now().toString(),
        desiredCnam: "",
        currentCnam: "",
        comment: "",
        phoneNumbers: "",
      },
    ]);
  };

  const removeBusinessEntry = (id: string) => {
    if (businessEntries.length === 1) return;
    setBusinessEntries(businessEntries.filter((entry) => entry.id !== id));
    setBusinessEntriesErrors(businessEntriesErrors.filter((e) => e.id !== id));
  };

  const updateBusinessEntry = (
    id: string,
    field: keyof BusinessEntry,
    value: string
  ) => {
    setBusinessEntries(
      businessEntries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
    setBusinessEntriesErrors(
      businessEntriesErrors.filter((e) => e.id !== id || e.key !== field)
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">Business CNAM (Many:1)</Typography>
        <Button
          variant="contained"
          startIcon={<FaPlus />}
          onClick={addBusinessEntry}
        >
          Add Business Entry
        </Button>
      </Box>
      {businessEntries.map((entry) => {
        const errorState = businessEntriesErrors.filter(
          (e) => e.id === entry.id
        );

        const errorStateKeys = errorState.map((e) => e.key);

        return (
          <Paper
            key={entry.id}
            sx={{ padding: 3, mb: 3, backgroundColor: "#f1f5f9" }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  fullWidth
                  label="Expected Business CNAM"
                  value={entry.desiredCnam}
                  onChange={(e) =>
                    updateBusinessEntry(entry.id, "desiredCnam", e.target.value)
                  }
                  inputProps={{ maxLength: 15 }}
                  error={
                    errorState !== undefined &&
                    errorStateKeys.includes("desiredCnam")
                  }
                  helperText={
                    errorStateKeys.includes("desiredCnam")
                      ? errorState.find((e) => e.key === "desiredCnam")?.error
                      : undefined
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Current CNAM"
                  value={entry.currentCnam}
                  onChange={(e) =>
                    updateBusinessEntry(entry.id, "currentCnam", e.target.value)
                  }
                  error={
                    errorState !== undefined &&
                    errorStateKeys.includes("currentCnam")
                  }
                  helperText={
                    errorStateKeys.includes("currentCnam")
                      ? errorState.find((e) => e.key === "currentCnam")?.error
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Comment"
                  value={entry.comment}
                  onChange={(e) =>
                    updateBusinessEntry(entry.id, "comment", e.target.value)
                  }
                  error={
                    errorState !== undefined &&
                    errorStateKeys.includes("comment")
                  }
                  helperText={
                    errorStateKeys.includes("comment")
                      ? errorState.find((e) => e.key === "comment")?.error
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "56px",
                  }}
                >
                  <IconButton
                    color="error"
                    onClick={() => removeBusinessEntry(entry.id)}
                    disabled={businessEntries.length === 1}
                  >
                    <FaTrash />
                  </IconButton>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={6}
                  label="Phone Numbers"
                  placeholder="15551234567&#10;15559876543&#10;15555555555"
                  value={entry.phoneNumbers}
                  onChange={(e) =>
                    updateBusinessEntry(
                      entry.id,
                      "phoneNumbers",
                      e.target.value
                    )
                  }
                  error={
                    errorState !== undefined &&
                    errorStateKeys.includes("phoneNumbers")
                  }
                  helperText={
                    errorStateKeys.includes("phoneNumbers")
                      ? errorState.find((e) => e.key === "phoneNumbers")?.error
                      : `Enter phone numbers one per line (max ${BUSINESS_MAX_UPLOADS_SIZE} numbers per business)`
                  }
                  required
                />
              </Grid>
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );
};
